// ContractTermsEditor.tsx - Contract Terms / Business Information / Owner
// Information sections of a contract send form. Controlled and presentational:
// the caller owns field values, the origination-fee state and every side
// effect (fetching, persistence, submission).
//
// Tooltips inside expect a `TooltipProvider` ancestor supplied by the caller,
// so the fields share one skip-delay group with anything rendered beside them.
import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import type { ContractKind, FormDataField } from "@/services/docuseal";
import { Input } from "@/components/ui/input";
import { FieldBorder, fieldBorderInputClassName } from "@/components/ui/field-border";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Overrides for backend field labels (which must match the DocuSeal template
// field names verbatim, e.g. "Merchant Email", "Business Address") so the UI
// can show shorter, section-scoped labels instead.
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  fed_id: "Fed ID/TIN/EIN",
  specified_percentage: "Specified Percentage of Revenue",
  merchant_email: "Email",
  owner_phone: "Phone Number",
  drivers_license_or_ssn: "Owner Drivers License",
  ssn: "SSN",
  business_address: "Address",
  business_city: "City",
  business_state: "State",
  business_zip: "Zip",
  owner_address: "Address",
  owner_city: "City",
  owner_state: "State",
  owner_zip: "Zip",
};

export const getDisplayLabel = (field: FormDataField): string =>
  FIELD_LABEL_OVERRIDES[field.field_name] ?? field.label ?? field.field_name;

const FIELD_EDIT_HELP_TEXT = "Edit on the Applications tab";

/** Form fields keyed by `field_name`. */
export type ContractFieldMap = Record<string, FormDataField>;

interface FieldProps {
  fieldName: string;
  fields: ContractFieldMap;
  values: Record<string, string>;
}

/**
 * A field mirroring data owned by the borrower's Application record. Editing
 * here would silently diverge from the source of truth, so it is always
 * read-only; the tooltip points brokers/underwriters back to the Applications
 * tab to make changes. Renders nothing when the form has no such field.
 */
export function ReadOnlyField({ fieldName, fields, values }: FieldProps) {
  const field = fields[fieldName];
  if (!field) return null;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <FieldBorder label={getDisplayLabel(field)} className="cursor-default select-none bg-muted">
          <Input
            id={fieldName}
            value={values[fieldName] ?? ""}
            readOnly
            tabIndex={-1}
            className={cn(fieldBorderInputClassName, "cursor-default text-muted-foreground")}
            disabled
          />
        </FieldBorder>
      </TooltipTrigger>
      <TooltipContent side="top">{FIELD_EDIT_HELP_TEXT}</TooltipContent>
    </Tooltip>
  );
}

interface ContractTermFieldProps extends FieldProps {
  helpText: string;
  className?: string;
}

function ContractTermField({
  fieldName,
  fields,
  values,
  helpText,
  className,
}: ContractTermFieldProps) {
  const field = fields[fieldName];
  if (!field) return null;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <FieldBorder
          label={getDisplayLabel(field)}
          className={cn("cursor-default select-none bg-muted", className)}
        >
          <div role="group" className="flex h-6 items-center text-sm text-muted-foreground">
            <span className="min-w-0 flex-1 truncate">{values[fieldName] ?? ""}</span>
          </div>
        </FieldBorder>
      </TooltipTrigger>
      <TooltipContent side="top">{helpText}</TooltipContent>
    </Tooltip>
  );
}

// "business_mailing_address" is intentionally not rendered: it's redundant
// with business_address/city/state/zip below, but the caller still prefills it
// in the values so it's submitted to DocuSeal on send. Same for "entity_type"
// - it's read-only/disabled and adds clutter, but stays in the values
// (prefilled from the fetched form data) so it still submits.
const merchantDetailKeysRow1 = ["merchants_legal_name", "merchants_dba", "fed_id"] as const;
const merchantDetailKeysRow4 = [
  "drivers_license_or_ssn",
  "ssn",
  "merchant_email",
  "owner_phone",
] as const;

export interface ContractTermsEditorProps {
  kind: ContractKind;
  fields: ContractFieldMap;
  /** Current field values keyed by `field_name`. */
  values: Record<string, string>;
  /** Disables the origination-fee controls (e.g. while a submission is active). */
  isLocked: boolean;
  /** Raw text of the origination-fee % input; the caller parses and clamps it. */
  originationFeeInput: string;
  /** Formatted origination-fee amount shown beside the % input. */
  originationFeeDisplay: string;
  onOriginationFeeInputChange: (raw: string) => void;
  /** Step the origination fee by `delta` percentage points. */
  onOriginationFeeStep: (delta: number) => void;
  /** Tooltip on the read-only contract-term fields (role-dependent copy). */
  contractTermsHelpText: string;
}

const ContractTermsEditor = React.memo(function ContractTermsEditor({
  kind,
  fields,
  values,
  isLocked,
  originationFeeInput,
  originationFeeDisplay,
  onOriginationFeeInputChange,
  onOriginationFeeStep,
  contractTermsHelpText,
}: ContractTermsEditorProps) {
  const termField = (fieldName: string, className?: string) => (
    <ContractTermField
      fieldName={fieldName}
      fields={fields}
      values={values}
      helpText={contractTermsHelpText}
      className={className}
    />
  );
  // Refinance keeps five equal columns, so those labels may still wrap.
  const oneLine = kind === "refinance_contract" ? undefined : "whitespace-nowrap";
  const readOnlyField = (fieldName: string) => (
    <ReadOnlyField fieldName={fieldName} fields={fields} values={values} />
  );

  return (
    <>
      <div>
        <h3 className="mb-3 text-base font-semibold">Contract Terms</h3>
        <div className="space-y-4">
          {/* "Total Amount to be Paid" stays on its label; disbursed takes the freed width. The fee track fits the stepper, %, and dollar amount. */}
          <div className="grid grid-cols-1 gap-4 min-[860px]:grid-cols-[210px_165px_minmax(136px,2fr)_minmax(193px,1fr)]">
            {termField("total_amount_funds_provided", "whitespace-nowrap")}
            {termField("total_amount_to_be_paid", "whitespace-nowrap")}
            {termField("amount_disbursed", "whitespace-nowrap")}
            <FieldBorder label="Origination Fee" className="whitespace-nowrap">
              <div className="flex h-6 items-center gap-2">
                <div className="flex shrink-0 items-center gap-0.5">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Increase origination fee by 0.25%"
                      onClick={() => onOriginationFeeStep(0.25)}
                      disabled={isLocked}
                      className="flex h-3 w-4 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      aria-label="Decrease origination fee by 0.25%"
                      onClick={() => onOriginationFeeStep(-0.25)}
                      disabled={isLocked}
                      className="flex h-3 w-4 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Input
                    type="text"
                    className={cn(fieldBorderInputClassName, "w-12 shrink-0 text-right")}
                    value={originationFeeInput}
                    onChange={(e) => onOriginationFeeInputChange(e.target.value)}
                    disabled={isLocked}
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
                <span className="shrink-0 text-muted-foreground">/</span>
                <span className="shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-foreground">
                  {originationFeeDisplay}
                </span>
              </div>
            </FieldBorder>
          </div>
          {/* Frequency is the short value. The revenue-percentage label takes the wide track so it stays one line. */}
          <div
            className={cn(
              "grid grid-cols-1 gap-4",
              kind === "refinance_contract"
                ? "sm:grid-cols-5"
                : "min-[860px]:grid-cols-[minmax(130px,0.9fr)_minmax(216px,1.7fr)_minmax(180px,1.25fr)_minmax(90px,0.55fr)]"
            )}
          >
            {termField("total_dollar_cost", oneLine)}
            {termField("specified_percentage", oneLine)}
            {termField("estimated_initial_payment", oneLine)}
            {termField("frequency", oneLine)}
            {kind === "refinance_contract" && termField("payoff_prior_balance")}
          </div>
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="mb-3 text-base font-semibold">Business Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {merchantDetailKeysRow1.map((key) => (
              <div key={key}>{readOnlyField(key)}</div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
            <div className="sm:col-span-3">{readOnlyField("business_address")}</div>
            {readOnlyField("business_city")}
            {readOnlyField("business_state")}
            {readOnlyField("business_zip")}
          </div>
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="mb-3 text-base font-semibold">Owner Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {merchantDetailKeysRow4.map((key) => (
              <div key={key}>{readOnlyField(key)}</div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
            <div className="sm:col-span-3">{readOnlyField("owner_address")}</div>
            {readOnlyField("owner_city")}
            {readOnlyField("owner_state")}
            {readOnlyField("owner_zip")}
          </div>
        </div>
      </div>
    </>
  );
});

export default ContractTermsEditor;
